import React, { useState, useEffect } from 'react';
import './PrinterTester.css';

const PrinterTester = () => {
  const [serverStatus, setServerStatus] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [printData, setPrintData] = useState('');
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});
  const [serverUrl, setServerUrl] = useState('http://localhost:6001');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [rawTestData, setRawTestData] = useState('');
  const [printSettings, setPrintSettings] = useState({
    leftMargin: 0,
    rightMargin: 0,
    lineSpacing: 1,
    fontSize: 'normal',
    alignment: 'left',
    cutPaper: true,
    feedLines: 3
  });

  // Test health check endpoint
  const testHealthCheck = async () => {
    setLoading(prev => ({ ...prev, health: true }));
    try {
      const response = await fetch(`${serverUrl}/`);
      const data = await response.json();
      setServerStatus(data);
      setResults(prev => ({ 
        ...prev, 
        health: { 
          success: true, 
          data, 
          timestamp: new Date().toISOString() 
        } 
      }));
    } catch (error) {
      setServerStatus(null);
      setResults(prev => ({ 
        ...prev, 
        health: { 
          success: false, 
          error: error.message, 
          timestamp: new Date().toISOString() 
        } 
      }));
    } finally {
      setLoading(prev => ({ ...prev, health: false }));
    }
  };

  // Test printers list endpoint
  const testPrintersList = async () => {
    setLoading(prev => ({ ...prev, printers: true }));
    try {
      const response = await fetch(`${serverUrl}/printers`);
      const data = await response.json();
      setPrinters(data.printers || []);
      setResults(prev => ({ 
        ...prev, 
        printers: { 
          success: true, 
          data, 
          timestamp: new Date().toISOString() 
        } 
      }));
    } catch (error) {
      setPrinters([]);
      setResults(prev => ({ 
        ...prev, 
        printers: { 
          success: false, 
          error: error.message, 
          timestamp: new Date().toISOString() 
        } 
      }));
    } finally {
      setLoading(prev => ({ ...prev, printers: false }));
    }
  };

  // Test print endpoint
  const testPrintEndpoint = async () => {
    if (!printData.trim()) {
      alert('Please enter base64 ESC/POS data');
      return;
    }

    setLoading(prev => ({ ...prev, print: true }));
    try {
      const response = await fetch(`${serverUrl}/print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: printData }),
      });
      const data = await response.json();
      setResults(prev => ({ 
        ...prev, 
        print: { 
          success: response.ok, 
          data, 
          timestamp: new Date().toISOString() 
        } 
      }));
    } catch (error) {
      setResults(prev => ({ 
        ...prev, 
        print: { 
          success: false, 
          error: error.message, 
          timestamp: new Date().toISOString() 
        } 
      }));
    } finally {
      setLoading(prev => ({ ...prev, print: false }));
    }
  };

  // Generate sample base64 ESC/POS data
  const generateSampleData = () => {
    // Simple ESC/POS commands for "TEST PRINT" - encoded in base64
    const escposCommands = [
      0x1B, 0x40, // Initialize printer
      0x1B, 0x61, 0x01, // Center align
      0x1B, 0x21, 0x30, // Double size text
      ...Array.from(new TextEncoder().encode('TEST PRINT\n')),
      0x1B, 0x21, 0x00, // Normal size text
      0x1B, 0x61, 0x00, // Left align
      ...Array.from(new TextEncoder().encode('This is a test from Printer Tester\n')),
      ...Array.from(new TextEncoder().encode('Timestamp: ' + new Date().toISOString() + '\n')),
      0x1B, 0x64, 0x03, // Feed 3 lines
      0x1D, 0x56, 0x42, 0x00 // Cut paper
    ];
    
    const buffer = new Uint8Array(escposCommands);
    const base64 = btoa(String.fromCharCode.apply(null, buffer));
    setPrintData(base64);
  };

  // Generate raw text data with formatting
  const generateRawData = () => {
    if (!rawTestData.trim()) {
      alert('Please enter some raw text data first');
      return;
    }

    const commands = [];
    
    // Initialize printer
    commands.push(0x1B, 0x40);
    
    // Set left margin
    if (printSettings.leftMargin > 0) {
      commands.push(0x1D, 0x4C, printSettings.leftMargin, 0x00);
    }
    
    // Set line spacing
    if (printSettings.lineSpacing !== 1) {
      const spacing = Math.round(printSettings.lineSpacing * 24); // 24 dots = 1 line
      commands.push(0x1B, 0x33, spacing);
    }
    
    // Set font size
    let fontCommand = 0x00;
    switch (printSettings.fontSize) {
      case 'small':
        fontCommand = 0x01;
        break;
      case 'large':
        fontCommand = 0x11;
        break;
      case 'double':
        fontCommand = 0x30;
        break;
      default:
        fontCommand = 0x00;
    }
    if (fontCommand !== 0x00) {
      commands.push(0x1B, 0x21, fontCommand);
    }
    
    // Set alignment
    let alignCommand = 0x00;
    switch (printSettings.alignment) {
      case 'center':
        alignCommand = 0x01;
        break;
      case 'right':
        alignCommand = 0x02;
        break;
      default:
        alignCommand = 0x00;
    }
    commands.push(0x1B, 0x61, alignCommand);
    
    // Add text content
    const lines = rawTestData.split('\n');
    lines.forEach((line, index) => {
      // Apply right margin by truncating line if needed
      let processedLine = line;
      if (printSettings.rightMargin > 0) {
        const maxWidth = 48 - printSettings.leftMargin - printSettings.rightMargin; // Assuming 48 char width
        if (processedLine.length > maxWidth) {
          processedLine = processedLine.substring(0, maxWidth);
        }
      }
      
      commands.push(...Array.from(new TextEncoder().encode(processedLine)));
      if (index < lines.length - 1) {
        commands.push(0x0A); // Line feed
      }
    });
    
    // Add final line feed
    commands.push(0x0A);
    
    // Feed lines
    if (printSettings.feedLines > 0) {
      commands.push(0x1B, 0x64, printSettings.feedLines);
    }
    
    // Cut paper if enabled
    if (printSettings.cutPaper) {
      commands.push(0x1D, 0x56, 0x42, 0x00);
    }
    
    const buffer = new Uint8Array(commands);
    const base64 = btoa(String.fromCharCode.apply(null, buffer));
    setPrintData(base64);
  };

  // Generate canvas-based raster data for precise ticket printing
  const generateCanvasRasterData = () => {
    // Create canvas - rotated dimensions (height becomes width for 90° rotation)
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size for 80mm width at 8 dots/mm (203dpi)
    const dotsPerMM = 8;
    const contentWidth = 550; // Content width (will become height after rotation) - increased to prevent text cutting
    const contentHeight = 80 * dotsPerMM; // 80mm (will become width after rotation)
    
    // Canvas dimensions for rotated content
    canvas.width = contentHeight; // 640 pixels (80mm)
    canvas.height = contentWidth; // 800 pixels
    
    // Fill with white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Save context state
    ctx.save();
    
    // Rotate canvas 90 degrees clockwise
    ctx.translate(canvas.width, 0);
    ctx.rotate(Math.PI / 2);
    
    // Now draw content in normal orientation (it will be rotated)
    ctx.fillStyle = 'black';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    
    // Add margins
    const leftMargin = 35 * dotsPerMM; // 10mm left margin (5mm original + 5mm shift right)
    let yPos = 15 * dotsPerMM; // 13mm top margin (10mm + 3mm)
    const lineHeight = 60;
    
    // Render ticket content
    // TICKET ID
    ctx.font = 'bold 32px Arial';
    ctx.fillText('TICKET ID', leftMargin, yPos);
    yPos += 40;
    ctx.font = '28px Arial';
    ctx.fillText('T19084186', leftMargin, yPos);
    yPos += lineHeight;
    
    // DATE & TIME
    ctx.font = 'bold 32px Arial';
    ctx.fillText('DATE & TIME', leftMargin, yPos);
    yPos += 40;
    ctx.font = '28px Arial';
    ctx.fillText('11/16/2025', leftMargin, yPos);
    yPos += 35;
    ctx.fillText('9:29:50 AM', leftMargin, yPos);
    yPos += lineHeight;
    
    // ITEM
    ctx.font = 'bold 32px Arial';
    ctx.fillText('ADULT X 1', leftMargin, yPos);
    yPos += 40;
    ctx.font = '28px Arial';
    ctx.fillText('B250', leftMargin, yPos);
    yPos += lineHeight;
    
    // TOTAL AMOUNT
    ctx.font = 'bold 32px Arial';
    ctx.fillText('TOTAL AMOUNT', leftMargin, yPos);
    yPos += 40;
    ctx.font = '28px Arial';
    ctx.fillText('B250', leftMargin, yPos);
    
    // Restore context
    ctx.restore();
    
    // Convert canvas to 1-bit bitmap (monochrome)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    
    // Convert to 1-bit monochrome
    const monoPixels = [];
    for (let i = 0; i < pixels.length; i += 4) {
      // Convert RGB to grayscale and threshold to 1-bit
      const gray = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;
      monoPixels.push(gray > 128 ? 0 : 1); // 0 = white, 1 = black
    }
    
    // Pack pixels into bytes (8 pixels per byte)
    const rasterData = [];
    for (let y = 0; y < canvas.height; y++) {
      const rowBytes = [];
      for (let x = 0; x < canvas.width; x += 8) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          if (x + bit < canvas.width) {
            const pixelIndex = y * canvas.width + (x + bit);
            if (monoPixels[pixelIndex]) {
              byte |= (0x80 >> bit); // Set bit for black pixel
            }
          }
        }
        rowBytes.push(byte);
      }
      rasterData.push(...rowBytes);
    }
    
    // Build ESC/POS commands with GS v 0 raster data
    const commands = [];
    
    // Initialize printer
    commands.push(0x1B, 0x40); // ESC @
    
    // Add 20mm blank space before printing (20mm = approximately 160 dots at 8 dots/mm)
    // Feed forward 20mm worth of lines
    const feedAmount = Math.floor(33 * dotsPerMM / 8); // Convert to line feeds (8 dots per line)
    if (feedAmount > 255) {
      // If more than 255 lines, break into multiple commands
      const fullFeeds = Math.floor(feedAmount / 255);
      const remainder = feedAmount % 255;
      for (let i = 0; i < fullFeeds; i++) {
        commands.push(0x1B, 0x64, 255); // Feed 255 lines multiple times
      }
      if (remainder > 0) {
        commands.push(0x1B, 0x64, remainder); // Feed remaining lines
      }
    } else {
      commands.push(0x1B, 0x64, feedAmount); // Feed lines for 20mm
    }
    
    // Send raster image using GS v 0
    const widthBytes = Math.ceil(canvas.width / 8);
    const widthL = widthBytes & 0xFF;
    const widthH = (widthBytes >> 8) & 0xFF;
    const heightL = canvas.height & 0xFF;
    const heightH = (canvas.height >> 8) & 0xFF;
    
    // GS v 0 command: Print raster bit image
    commands.push(0x1D, 0x76, 0x30, 0x00); // GS v 0 m
    commands.push(widthL, widthH); // Width in bytes (little endian)
    commands.push(heightL, heightH); // Height in dots (little endian)
    
    // Add raster data
    commands.push(...rasterData);
    
    // Feed and cut
    commands.push(0x1B, 0x64, 0x03); // Feed 3 lines
    commands.push(0x1D, 0x56, 0x42, 0x00); // Cut paper
    
    // Convert to base64
    const buffer = new Uint8Array(commands);
    const base64 = btoa(String.fromCharCode.apply(null, buffer));
    setPrintData(base64);
    
    // Optional: Show canvas preview (for debugging)
    const previewContainer = document.getElementById('canvas-preview');
    if (!previewContainer) {
      const container = document.createElement('div');
      container.id = 'canvas-preview';
      container.style.cssText = `
        position: fixed;
        top: 50px;
        right: 20px;
        background: white;
        border: 2px solid #333;
        padding: 10px;
        z-index: 1000;
        max-width: 500px;
      `;
      document.body.appendChild(container);
    }
    
    const preview = document.getElementById('canvas-preview');
    preview.innerHTML = '<h4>Canvas Preview:</h4>';
    canvas.style.width = '100%';
    canvas.style.border = '1px solid #ccc';
    preview.appendChild(canvas);
  };

  // Generate complex test patterns
  const generateTestPattern = (patternType) => {
    const commands = [];
    commands.push(0x1B, 0x40); // Initialize
    
    switch (patternType) {
      case 'receipt':
        // Receipt header
        commands.push(0x1B, 0x61, 0x01); // Center
        commands.push(0x1B, 0x21, 0x30); // Double size
        commands.push(...Array.from(new TextEncoder().encode('STORE NAME\n')));
        commands.push(0x1B, 0x21, 0x00); // Normal
        commands.push(...Array.from(new TextEncoder().encode('123 Main Street\n')));
        commands.push(...Array.from(new TextEncoder().encode('City, State 12345\n')));
        commands.push(...Array.from(new TextEncoder().encode('Tel: (555) 123-4567\n\n')));
        
        // Receipt body
        commands.push(0x1B, 0x61, 0x00); // Left align
        commands.push(...Array.from(new TextEncoder().encode('Date: ' + new Date().toLocaleDateString() + '\n')));
        commands.push(...Array.from(new TextEncoder().encode('Time: ' + new Date().toLocaleTimeString() + '\n')));
        commands.push(...Array.from(new TextEncoder().encode('Cashier: Test User\n\n')));
        commands.push(...Array.from(new TextEncoder().encode('--------------------------------\n')));
        commands.push(...Array.from(new TextEncoder().encode('Item 1                     $5.99\n')));
        commands.push(...Array.from(new TextEncoder().encode('Item 2                     $3.50\n')));
        commands.push(...Array.from(new TextEncoder().encode('Item 3                     $2.25\n')));
        commands.push(...Array.from(new TextEncoder().encode('--------------------------------\n')));
        commands.push(...Array.from(new TextEncoder().encode('Subtotal:                 $11.74\n')));
        commands.push(...Array.from(new TextEncoder().encode('Tax:                       $0.94\n')));
        commands.push(...Array.from(new TextEncoder().encode('Total:                    $12.68\n\n')));
        commands.push(0x1B, 0x61, 0x01); // Center
        commands.push(...Array.from(new TextEncoder().encode('Thank you for your visit!\n')));
        break;
        
      case 'barcode':
        commands.push(0x1B, 0x61, 0x01); // Center
        commands.push(...Array.from(new TextEncoder().encode('BARCODE TEST\n\n')));
        // GS1-128 barcode
        commands.push(0x1D, 0x6B, 0x49, 0x0C, 0x7B, 0x42, 0x31, 0x32, 0x33, 0x34, 0x35, 0x36, 0x37, 0x38, 0x39, 0x30);
        commands.push(...Array.from(new TextEncoder().encode('\n1234567890\n')));
        break;
        
      case 'formatting':
        // Test various formatting options
        commands.push(...Array.from(new TextEncoder().encode('=== FORMATTING TEST ===\n\n')));
        
        // Normal text
        commands.push(0x1B, 0x21, 0x00);
        commands.push(...Array.from(new TextEncoder().encode('Normal text\n')));
        
        // Bold
        commands.push(0x1B, 0x45, 0x01);
        commands.push(...Array.from(new TextEncoder().encode('Bold text\n')));
        commands.push(0x1B, 0x45, 0x00);
        
        // Underline
        commands.push(0x1B, 0x2D, 0x01);
        commands.push(...Array.from(new TextEncoder().encode('Underlined text\n')));
        commands.push(0x1B, 0x2D, 0x00);
        
        // Different sizes
        commands.push(0x1B, 0x21, 0x01);
        commands.push(...Array.from(new TextEncoder().encode('Small text\n')));
        commands.push(0x1B, 0x21, 0x11);
        commands.push(...Array.from(new TextEncoder().encode('Large text\n')));
        commands.push(0x1B, 0x21, 0x30);
        commands.push(...Array.from(new TextEncoder().encode('Double text\n')));
        
        // Reset
        commands.push(0x1B, 0x21, 0x00);
        
        // Alignments
        commands.push(0x1B, 0x61, 0x00);
        commands.push(...Array.from(new TextEncoder().encode('Left aligned\n')));
        commands.push(0x1B, 0x61, 0x01);
        commands.push(...Array.from(new TextEncoder().encode('Center aligned\n')));
        commands.push(0x1B, 0x61, 0x02);
        commands.push(...Array.from(new TextEncoder().encode('Right aligned\n')));
        break;
        
      case 'boardingpass':
        // Attraction Ticket - Rotated 90 degrees (text prints vertically on 80mm paper)
        commands.push(0x1B, 0x40); // Initialize printer
        
        // Try different rotation command - GS ( k for page mode rotation
        // Enter page mode
        commands.push(0x1B, 0x4C); // ESC L - Enter page mode
        
        // Set print direction in page mode (rotate 90 degrees)
        commands.push(0x1B, 0x54, 0x01); // ESC T n - Set print direction (1 = 90 degrees clockwise)
        
        // Set print area
        commands.push(0x1B, 0x57, 0x00, 0x00, 0x00, 0x00, 0x40, 0x02, 0x00, 0x03); // Set print area
        
        // Left align
        commands.push(0x1B, 0x61, 0x00); // Left align
        
        // Add spacing
        commands.push(...Array.from(new TextEncoder().encode('\n\n')));
        
        // Print ticket info - will appear rotated 90 degrees
        commands.push(0x1B, 0x21, 0x08); // Bold text
        commands.push(...Array.from(new TextEncoder().encode('TICKET ID\n')));
        commands.push(0x1B, 0x21, 0x00); // Normal text
        commands.push(...Array.from(new TextEncoder().encode('T19084186\n\n')));
        
        commands.push(0x1B, 0x21, 0x08); // Bold text
        commands.push(...Array.from(new TextEncoder().encode('DATE & TIME\n')));
        commands.push(0x1B, 0x21, 0x00); // Normal text
        commands.push(...Array.from(new TextEncoder().encode('11/16/2025\n')));
        commands.push(...Array.from(new TextEncoder().encode('9:29:50 AM\n\n')));
        
        commands.push(0x1B, 0x21, 0x08); // Bold text
        commands.push(...Array.from(new TextEncoder().encode('ADULT X 1\n')));
        commands.push(0x1B, 0x21, 0x00); // Normal text
        commands.push(...Array.from(new TextEncoder().encode('B250\n\n')));
        
        commands.push(0x1B, 0x21, 0x08); // Bold text
        commands.push(...Array.from(new TextEncoder().encode('TOTAL AMOUNT\n')));
        commands.push(0x1B, 0x21, 0x00); // Normal text
        commands.push(...Array.from(new TextEncoder().encode('B250\n')));
        
        // Exit page mode and print
        commands.push(0x1B, 0x53); // ESC S - Exit page mode and print
        
        break;
    }
    
    commands.push(0x1B, 0x64, 0x03); // Feed lines
    commands.push(0x1D, 0x56, 0x42, 0x00); // Cut paper
    
    const buffer = new Uint8Array(commands);
    const base64 = btoa(String.fromCharCode.apply(null, buffer));
    setPrintData(base64);
  };

  // Auto-test health check on component mount
  useEffect(() => {
    testHealthCheck();
  }, [serverUrl]);

  return (
    <div className="printer-tester">
      <div className="header">
        <h1>🖨️ Thermal Printer Agent Tester</h1>
        <p>Test the printer agent endpoints without authentication</p>
      </div>

      {/* Server Configuration */}
      <div className="section">
        <h2>Server Configuration</h2>
        <div className="config-group">
          <label>
            Server URL:
            <input
              type="text"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
              placeholder="http://localhost:5001"
            />
          </label>
        </div>
      </div>

      {/* Health Check Section */}
      <div className="section">
        <h2>1. Health Check (GET /)</h2>
        <p>Test if the server is running and get service information</p>
        
        <button 
          onClick={testHealthCheck}
          disabled={loading.health}
          className="test-button"
        >
          {loading.health ? 'Testing...' : 'Test Health Check'}
        </button>

        {serverStatus && (
          <div className="status-display">
            <h3>Server Status:</h3>
            <div className={`status-badge ${serverStatus.status}`}>
              {serverStatus.status.toUpperCase()}
            </div>
            <div className="status-details">
              <p><strong>Service:</strong> {serverStatus.service}</p>
              <p><strong>Version:</strong> {serverStatus.version}</p>
            </div>
          </div>
        )}

        {results.health && (
          <div className={`result ${results.health.success ? 'success' : 'error'}`}>
            <h4>Result:</h4>
            <pre>{JSON.stringify(results.health, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Printers List Section */}
      <div className="section">
        <h2>2. List Printers (GET /printers)</h2>
        <p>Get available printers on the system</p>
        
        <button 
          onClick={testPrintersList}
          disabled={loading.printers}
          className="test-button"
        >
          {loading.printers ? 'Testing...' : 'Get Printers List'}
        </button>

        {printers.length > 0 && (
          <div className="printers-list">
            <h3>Available Printers:</h3>
            <ul>
              {printers.map((printer, index) => (
                <li key={index} className="printer-item">
                  <strong>{printer.name}</strong>
                  {printer.isDefault && <span className="default-badge">DEFAULT</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {results.printers && (
          <div className={`result ${results.printers.success ? 'success' : 'error'}`}>
            <h4>Result:</h4>
            <pre>{JSON.stringify(results.printers, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Print Test Section */}
      <div className="section">
        <h2>3. Print Test (POST /print)</h2>
        <p>Send base64 encoded ESC/POS data to print</p>
        
        <div className="print-controls">
          <div className="button-group">
            <button 
              onClick={generateSampleData}
              className="sample-button"
            >
              Generate Sample Data
            </button>
            
            <button 
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="advanced-button"
            >
              {showAdvanced ? 'Hide Advanced' : 'Show Advanced Options'}
            </button>
          </div>

          {/* Advanced Options */}
          {showAdvanced && (
            <div className="advanced-section">
              <h3>🔧 Advanced Testing Options</h3>
              
              {/* Raw Text Input */}
              <div className="raw-text-section">
                <h4>Raw Text with Formatting</h4>
                <textarea
                  value={rawTestData}
                  onChange={(e) => setRawTestData(e.target.value)}
                  placeholder="Enter raw text to print with formatting options..."
                  rows="3"
                  className="raw-text-input"
                />
                
                {/* Print Settings */}
                <div className="print-settings">
                  <div className="settings-grid">
                    <div className="setting-group">
                      <label>Left Margin (chars):</label>
                      <input
                        type="number"
                        min="0"
                        max="20"
                        value={printSettings.leftMargin}
                        onChange={(e) => setPrintSettings(prev => ({...prev, leftMargin: parseInt(e.target.value) || 0}))}
                      />
                    </div>
                    
                    <div className="setting-group">
                      <label>Right Margin (chars):</label>
                      <input
                        type="number"
                        min="0"
                        max="20"
                        value={printSettings.rightMargin}
                        onChange={(e) => setPrintSettings(prev => ({...prev, rightMargin: parseInt(e.target.value) || 0}))}
                      />
                    </div>
                    
                    <div className="setting-group">
                      <label>Line Spacing:</label>
                      <select
                        value={printSettings.lineSpacing}
                        onChange={(e) => setPrintSettings(prev => ({...prev, lineSpacing: parseFloat(e.target.value)}))}
                      >
                        <option value={1}>Normal (1x)</option>
                        <option value={1.5}>1.5x</option>
                        <option value={2}>Double (2x)</option>
                        <option value={2.5}>2.5x</option>
                        <option value={3}>Triple (3x)</option>
                      </select>
                    </div>
                    
                    <div className="setting-group">
                      <label>Font Size:</label>
                      <select
                        value={printSettings.fontSize}
                        onChange={(e) => setPrintSettings(prev => ({...prev, fontSize: e.target.value}))}
                      >
                        <option value="small">Small</option>
                        <option value="normal">Normal</option>
                        <option value="large">Large</option>
                        <option value="double">Double</option>
                      </select>
                    </div>
                    
                    <div className="setting-group">
                      <label>Alignment:</label>
                      <select
                        value={printSettings.alignment}
                        onChange={(e) => setPrintSettings(prev => ({...prev, alignment: e.target.value}))}
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                    
                    <div className="setting-group">
                      <label>Feed Lines:</label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={printSettings.feedLines}
                        onChange={(e) => setPrintSettings(prev => ({...prev, feedLines: parseInt(e.target.value) || 0}))}
                      />
                    </div>
                    
                    <div className="setting-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={printSettings.cutPaper}
                          onChange={(e) => setPrintSettings(prev => ({...prev, cutPaper: e.target.checked}))}
                        />
                        Cut Paper
                      </label>
                    </div>
                  </div>
                  
                  <button 
                    onClick={generateRawData}
                    className="generate-raw-button"
                    disabled={!rawTestData.trim()}
                  >
                    Generate Formatted Data
                  </button>
                </div>
              </div>

              {/* Test Patterns */}
              <div className="test-patterns-section">
                <h4>Test Patterns</h4>
                <div className="pattern-buttons">
                  <button 
                    onClick={() => generateTestPattern('receipt')}
                    className="pattern-button receipt"
                  >
                    📄 Sample Receipt
                  </button>
                  
                  <button 
                    onClick={() => generateTestPattern('barcode')}
                    className="pattern-button barcode"
                  >
                    📊 Barcode Test
                  </button>
                  
                  <button 
                    onClick={() => generateTestPattern('formatting')}
                    className="pattern-button formatting"
                  >
                    🎨 Formatting Test
                  </button>
                  
                  <button 
                    onClick={() => generateTestPattern('boardingpass')}
                    className="pattern-button boardingpass"
                  >
                    🎫 Ticket (80mm Rotated 90°)
                  </button>
                  
                  <button 
                    onClick={generateCanvasRasterData}
                    className="pattern-button canvas"
                  >
                    🖼️ Canvas Raster (80mm Rotated 90°)
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <textarea
            value={printData}
            onChange={(e) => setPrintData(e.target.value)}
            placeholder="Enter base64 encoded ESC/POS data here..."
            rows="4"
            className="print-data-input"
          />
          
          <button 
            onClick={testPrintEndpoint}
            disabled={loading.print || !printData.trim()}
            className="test-button print-button"
          >
            {loading.print ? 'Printing...' : 'Send Print Job'}
          </button>
        </div>

        {results.print && (
          <div className={`result ${results.print.success ? 'success' : 'error'}`}>
            <h4>Print Result:</h4>
            <pre>{JSON.stringify(results.print, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Test History */}
      <div className="section">
        <h2>Test History</h2>
        <div className="test-history">
          {Object.entries(results).map(([test, result]) => (
            <div key={test} className={`history-item ${result.success ? 'success' : 'error'}`}>
              <div className="history-header">
                <span className="test-name">{test.toUpperCase()}</span>
                <span className="timestamp">{new Date(result.timestamp).toLocaleString()}</span>
                <span className={`status-indicator ${result.success ? 'success' : 'error'}`}>
                  {result.success ? '✅' : '❌'}
                </span>
              </div>
              {result.error && (
                <div className="error-message">
                  Error: {result.error}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Documentation */}
      <div className="section documentation">
        <h2>📖 API Documentation</h2>
        <div className="endpoint-docs">
          <div className="endpoint">
            <h3>GET /</h3>
            <p>Health check endpoint that returns server status and available endpoints.</p>
          </div>
          
          <div className="endpoint">
            <h3>GET /printers</h3>
            <p>Lists all available printers on the system.</p>
          </div>
          
          <div className="endpoint">
            <h3>POST /print</h3>
            <p>Accepts base64 encoded ESC/POS data and sends it to the printer.</p>
            <div className="endpoint-example">
              <strong>Body:</strong>
              <pre>{"{\n  \"data\": \"<base64 encoded ESC/POS>\"\n}"}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrinterTester;